import React, { useCallback, useState } from 'react'
import { useIntl } from 'react-intl'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { searchFlights } from 'api/flights'
import { generateFlightCheckoutItem } from 'lib/checkout/flights/cart'
import { generatePassengersFromOccupants } from 'checkout/lib/utils/flights/passenger'
import { addToCheckoutCart } from 'actions/CheckoutActions'
import { navigateToCheckout } from 'checkout/actions/navigateActions'
import { FLIGHTS_SESSION_EXPIRATION_TIME_IN_MINUTES } from 'constants/flight'
import { showSnackbar } from 'LuxKit/Snackbar/AppSnackbar'
import FareFamilySelectModal from 'components/Flights/V2/FareFamily/FareFamilySelectModal'
import { type TransportOption, isLeFlightSavedItemData } from 'tripPlanner/api/types'
import type { ModalContextType } from 'contexts/ModalContext'
import { parseSearchParams } from './flightShortlistUtils'
import { getCachedJourney } from './shortlistedJourneyCache'

interface UseBookResult {
  bookFlight: (option: TransportOption) => void
  bookingItemId: number | undefined
  searchingItemId: number | undefined
}

interface FareFamilyModalResult {
  fareFamily?: App.FlightFareFamily
  returningFareFamily?: App.FlightFareFamily
  addToUserCart?: boolean
}

function isBookableCachedJourney(entry?: { journey: App.JourneyV2; searchId?: string }): boolean {
  if (!entry?.searchId) return false

  const firstFlight = entry.journey.flightGroup?.flights?.[0]
  return !!(
    entry.journey.price?.all?.totalFare != null
    && firstFlight?.marketingAirline?.code
  )
}

function useBookShortlistedFlight(showModal: ModalContextType): UseBookResult {
  const [bookingItemId, setBookingItemId] = useState<number | undefined>(undefined)
  const [searchingItemId, setSearchingItemId] = useState<number | undefined>(undefined)
  const intl = useIntl()
  const dispatch = useAppDispatch()
  const currency = useAppSelector(state => state.geo.currentCurrency)
  const region = useAppSelector(state => state.geo.currentRegionCode)
  const email = useAppSelector(state => state.auth.account.email)

  const bookFlight = useCallback(async(option: TransportOption) => {
    if (!isLeFlightSavedItemData(option.savedItemData)) return

    const data = option.savedItemData
    const params = parseSearchParams(data.searchParams)
    if (!params) {
      showSnackbar(intl.formatMessage({
        id: 'R6wd52-oZj65JhpjjL7G',
        defaultMessage: 'Unable to book this flight. Search parameters are missing.',
        description: 'Error snackbar shown when a shortlisted flight is missing search parameters needed for booking',
      }), 'critical')
      return
    }

    setBookingItemId(option.itemId)

    try {
      const returnLeg = option.returnLeg
      const returnLegData = isLeFlightSavedItemData(returnLeg?.savedItemData) ? returnLeg.savedItemData : undefined
      const isReturn = !!returnLeg && !!returnLegData
      const fareType = params.fareType === 'return' ? 'return' as const : 'oneWay' as const

      const flightUnavailableMessage = intl.formatMessage({
        id: 'hqp07ZRkaLAescD38LIo',
        defaultMessage: 'This flight is no longer available. Please search for new flights.',
        description: 'Warning snackbar shown when a shortlisted flight cannot be found in current search results',
      })

      let matchedDepartingJourney: App.JourneyV2 | undefined
      let matchedReturningJourney: App.JourneyV2 | undefined
      let searchId: string | undefined
      let sessionId: string | undefined
      let searchExpiry: number

      const departingCached = data.journeyKey ? getCachedJourney(data.journeyKey) : undefined
      const returningCached = returnLegData?.journeyKey ? getCachedJourney(returnLegData.journeyKey) : undefined

      const departingCacheValid = !!departingCached && isBookableCachedJourney(departingCached)
      const returningCacheValid = !isReturn || (!!returningCached && isBookableCachedJourney(returningCached))

      if (departingCached && departingCacheValid && returningCacheValid) {
        matchedDepartingJourney = departingCached.journey
        matchedReturningJourney = returningCached?.journey
        searchId = departingCached.searchId
        sessionId = departingCached.sessionId
        searchExpiry = departingCached.cachedAt + FLIGHTS_SESSION_EXPIRATION_TIME_IN_MINUTES * 60 * 1000
      } else {
        setSearchingItemId(option.itemId)

        const searchResult = await searchFlights({
          itinerary: params.itinerary,
          currency,
          occupancies: params.occupancies,
          fareClass: params.fareClass,
          region,
          fareType: params.fareType,
          email,
        }, { enableSabreContent: true })

        setSearchingItemId(undefined)
        searchId = searchResult.searchId
        sessionId = searchResult.sessionId
        searchExpiry = Date.now() + FLIGHTS_SESSION_EXPIRATION_TIME_IN_MINUTES * 60 * 1000

        if (isReturn) {
          const departingFares = searchResult.onewayFares[0] ?? []
          const returningFares = searchResult.onewayFares[1] ?? []

          matchedDepartingJourney = data.journeyKey
            ? departingFares.find(j => j.bookingInfo.journeyKey === data.journeyKey)
            : undefined

          matchedReturningJourney = returnLegData.journeyKey
            ? returningFares.find(j => j.bookingInfo.journeyKey === returnLegData.journeyKey)
            : undefined

          if (!matchedDepartingJourney || !matchedReturningJourney) {
            showSnackbar(flightUnavailableMessage, 'warning')
            return
          }
        } else {
          const allFares = searchResult.onewayFares.flat()
          matchedDepartingJourney = data.journeyKey
            ? allFares.find(j => j.bookingInfo.journeyKey === data.journeyKey)
            : undefined

          if (!matchedDepartingJourney) {
            showSnackbar(flightUnavailableMessage, 'warning')
            return
          }
        }
      }

      if (!searchId) {
        showSnackbar(flightUnavailableMessage, 'warning')
        return
      }

      const occupants = params.occupancies[0] ?? { adults: 1 }
      const passengers = generatePassengersFromOccupants(occupants)

      let departingFareFamily: App.FlightFareFamily | undefined
      let returningFareFamily: App.FlightFareFamily | undefined

      if (isReturn) {
        if (matchedDepartingJourney.fareFamily) {
          const departResult = await showModal<FareFamilyModalResult>(
            React.createElement(FareFamilySelectModal, {
              journey: matchedDepartingJourney,
              occupancies: occupants,
              searchId,
              sessionId,
              relativePrice: matchedDepartingJourney.price.all.totalFare,
              isStandalone: true,
            }),
          )

          if (!departResult) return
          departingFareFamily = departResult.fareFamily
        }

        if (matchedReturningJourney!.fareFamily) {
          const returnResult = await showModal<FareFamilyModalResult>(
            React.createElement(FareFamilySelectModal, {
              journey: matchedReturningJourney!,
              departingJourney: matchedDepartingJourney,
              departingFareFamily,
              occupancies: occupants,
              searchId,
              sessionId,
              relativePrice: matchedReturningJourney!.price.all.totalFare,
              isStandalone: true,
            }),
          )

          if (!returnResult) return
          returningFareFamily = returnResult.fareFamily
        }
      } else if (matchedDepartingJourney.fareFamily) {
        const result = await showModal<FareFamilyModalResult>(
          React.createElement(FareFamilySelectModal, {
            journey: matchedDepartingJourney,
            occupancies: occupants,
            searchId,
            sessionId,
            relativePrice: matchedDepartingJourney.price.all.totalFare,
            isStandalone: true,
          }),
        )

        if (!result) return
        departingFareFamily = result.fareFamily
      }

      const flights = isReturn
        ? [matchedDepartingJourney, matchedReturningJourney!]
        : [matchedDepartingJourney]

      const fareFamilies = isReturn
        ? [departingFareFamily, returningFareFamily]
        : [departingFareFamily]

      const checkoutItem = generateFlightCheckoutItem({
        originAirportCode: params.itinerary[0].departureAirport,
        destinationAirportCode: params.itinerary[0].arrivalAirport,
        occupants,
        passengers,
        fareType,
        viewType: fareType === 'oneWay' ? 'OneWay' : 'TwoOneWays',
        searchId,
        flights,
        baggage: [],
        fareFamilies,
        bundledItemIds: [],
        expiry: searchExpiry,
      })

      dispatch(addToCheckoutCart([checkoutItem]))
      dispatch(navigateToCheckout())
    } catch {
      showSnackbar(intl.formatMessage({
        id: 'Xi8uEjkdq0mQ8_7rn8QW',
        defaultMessage: 'Something went wrong while preparing your booking. Please try again.',
        description: 'Error snackbar shown when booking a shortlisted flight fails unexpectedly',
      }), 'critical')
    } finally {
      setBookingItemId(undefined)
      setSearchingItemId(undefined)
    }
  }, [dispatch, currency, region, showModal, intl, email])

  return { bookFlight, bookingItemId, searchingItemId }
}

export default useBookShortlistedFlight
